/* eslint-disable @typescript-eslint/no-unused-vars */

import { pad } from 'utils/utils.misc';

/* eslint-disable prefer-const */
type Rgb = { r: number; g: number; b: number };

const hexToDec = (hex: string) => parseInt(hex, 16);
const decToHex = (dec: number) => Math.round(dec).toString(16).padStart(2, '0');

export const hexToRgb = (hex: string) => {
    return {
        r: hexToDec(hex.substring(1, 3)),
        g: hexToDec(hex.substring(3, 5)),
        b: hexToDec(hex.substring(5, 7)),
    };
};

const getDarkShadeFromRgb = ({ r, g, b }: Rgb, i: number) =>
    `#${decToHex((1 - i) * r)}${decToHex((1 - i) * g)}${decToHex((1 - i) * b)}`;
const getLightShadeFromRgb = ({ r, g, b }: Rgb, i: number) =>
    `#${decToHex(r + (255 - r) * i)}${decToHex(g + (255 - g) * i)}${decToHex(b + (255 - b) * i)}`;

/**
 * Darkens the color following a linear distorsion
 * @param hex Color in hexadecimal format, beginning with the # character
 * @param darkPercentage dark percentage (bewteen 0 and 1, 0 beeing the same color, 1 beeing black)
 * @returns hexadecimal color
 */
const getDarkShade = (hex: string, darkPercentage: number) => getDarkShadeFromRgb(hexToRgb(hex), darkPercentage);

/**
 * Lightens the color following a linear distorsion
 * @param hex Color in hexadecimal format, beginning with the # character
 * @param lightPercentage light percentage (bewteen 0 and 1, 0 beeing the same color, 1 beeing white)
 * @returns hexadecimal color
 */
const getLightShade = (hex: string, lightPercentage: number) => getLightShadeFromRgb(hexToRgb(hex), lightPercentage);

export const HSBToRGB = (hue: number, saturation: number, brightness: number) => {
    const k = (n: number) => (n + hue / 60) % 6;
    const f = (n: number) => brightness * (1 - saturation * Math.max(0, Math.min(k(n), 4 - k(n), 1)));
    return [Math.round(255 * f(5)), Math.round(255 * f(3)), Math.round(255 * f(1))];
};

// Red, Blue, Green to Hue, Saturation, Brightness
export const RGBToHSB = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const v = Math.max(r, g, b),
        n = v - Math.min(r, g, b);
    const h = n === 0 ? 0 : n && v === r ? (g - b) / n : v === g ? 2 + (b - r) / n : 4 + (r - g) / n;
    return [Math.round(60 * (h < 0 ? h + 6 : h)), Math.round(v && (n / v) * 100), Math.round(v * 100)];
};

export const hexToHSL = (H: any) => {
    let r: any = 0,
        g: any = 0,
        b: any = 0;
    if (H.length == 4) {
        r = '0x' + H[1] + H[1];
        g = '0x' + H[2] + H[2];
        b = '0x' + H[3] + H[3];
    } else if (H.length == 7) {
        r = '0x' + H[1] + H[2];
        g = '0x' + H[3] + H[4];
        b = '0x' + H[5] + H[6];
    }
    r /= 255;
    g /= 255;
    b /= 255;
    let cmin = Math.min(r, g, b),
        cmax = Math.max(r, g, b),
        delta = cmax - cmin,
        h = 0,
        s = 0,
        l = 0;

    if (delta == 0) {
        h = 0;
    } else if (cmax == r) {
        h = ((g - b) / delta) % 6;
    } else if (cmax == g) {
        h = (b - r) / delta + 2;
    } else {
        h = (r - g) / delta + 4;
    }

    h = Math.round(h * 60);

    if (h < 0) {
        h += 360;
    }

    l = (cmax + cmin) / 2;
    s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    s = +(s * 100).toFixed(1);
    l = +(l * 100).toFixed(1);

    // return "hsl(" + h + "," + s + "%," + l + "%)";
    return [h, s, l];
};

export function hlsToHex(h: number, s: number, l: number) {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0,
        g = 0,
        b = 0;

    if (0 <= h && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (60 <= h && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (120 <= h && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (180 <= h && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (240 <= h && h < 300) {
        r = x;
        g = 0;
        b = c;
    } else if (300 <= h && h < 360) {
        r = c;
        g = 0;
        b = x;
    }

    return [
        pad(Math.round((r + m) * 255).toString(16)),
        pad(Math.round((g + m) * 255).toString(16)),
        pad(Math.round((b + m) * 255).toString(16)),
    ];
}
/*
export const hexAToHSLA = (H: any) => {
    let r: any = 0,
        g: any = 0,
        b: any = 0,
        a: any = 1;

    if (H.length == 5) {
        r = '0x' + H[1] + H[1];
        g = '0x' + H[2] + H[2];
        b = '0x' + H[3] + H[3];
        a = '0x' + H[4] + H[4];
    } else if (H.length == 9) {
        r = '0x' + H[1] + H[2];
        g = '0x' + H[3] + H[4];
        b = '0x' + H[5] + H[6];
        a = '0x' + H[7] + H[8];
    }

    r /= 255;
    g /= 255;
    b /= 255;
    let cmin = Math.min(r, g, b),
        cmax = Math.max(r, g, b),
        delta = cmax - cmin,
        h = 0,
        s = 0,
        l = 0;

    if (delta == 0) {
        h = 0;
    } else if (cmax == r) {
        h = ((g - b) / delta) % 6;
    } else if (cmax == g) {
        h = (b - r) / delta + 2;
    } else {
        h = (r - g) / delta + 4;
    }

    h = Math.round(h * 60);

    if (h < 0) {
        h += 360;
    }

    l = (cmax + cmin) / 2;
    s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    s = +(s * 100).toFixed(1);
    l = +(l * 100).toFixed(1);

    a = (a / 255).toFixed(3);

    // return "hsla("+ h + "," + s + "%," + l + "%," + a + ")";
    return [h, s, l];
};
*/
export const RGBToHSL = (rgb: string) => {
    const sep = rgb.indexOf(',') > -1 ? ',' : ' ';
    const rgbArray = rgb.substring(4).split(')')[0].split(sep);
    const rgbNumArray = [0, 0, 0];

    for (const R in rgbArray) {
        rgbArray[R] = rgbArray[R].trim();
        const r = rgbArray[R];
        if (r.indexOf('%') > -1) {
            rgbNumArray[R] = Math.round((+r.substring(0, r.length - 1) / 100) * 255);
        } else {
            rgbNumArray[R] = +rgbArray[R];
        }
    }

    const r = rgbNumArray[0] / 255,
        g = rgbNumArray[1] / 255,
        b = rgbNumArray[2] / 255;

    let cmin = Math.min(r, g, b),
        cmax = Math.max(r, g, b),
        delta = cmax - cmin,
        h = 0,
        s = 0,
        l = 0;

    l = (cmax + cmin) / 2;

    s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

    s = +(s * 100).toFixed(1);
    l = +(l * 100).toFixed(1);

    // return "hsl(" + h + "," + s + "%," + l + "%)";
    return [h, s, l];
};

const HEXRegexString = '^#(?:[A-Fa-f0-9]{3}){1,2}$';
// Detects rgb(123,23,255) format and rgb(12%, 23%, 98.6%) format
const RGBRegexString =
    '^rgb\\(\\s*(25[0-5]|2[0-4][0-9]|1?\\d{1,2})\\s*,\\s*(25[0-5]|2[0-4][0-9]|1?\\d{1,2})\\s*,\\s*(25[0-5]|2[0-4][0-9]|1?\\d{1,2})\\s*\\)$|^rgb\\(\\s*(\\d{1,3}(?:\\.\\d+)?%)\\s*,\\s*(\\d{1,3}(?:\\.\\d+)?%)\\s*,\\s*(\\d{1,3}(?:\\.\\d+)?%)\\s*\\)$';
const HSLRegexString =
    '^hsl\\(\\s*(3[0-5][0-9]|360|[12]?\\d{1,2})\\s*,\\s*(100(?:\\.0+)?|\\d{1,2}(?:\\.\\d+)?)%\\s*,\\s*(100(?:\\.0+)?|\\d{1,2}(?:\\.\\d+)?)%\\s*\\)$';

const colorContrast = (color: string): string => {
    const regexHEX = new RegExp(HEXRegexString);
    const regexRGB = new RegExp(RGBRegexString);
    const regexHSL = new RegExp(HSLRegexString);

    const hsl: undefined | boolean | any[] = regexHEX.test(color)
        ? hexToHSL(color)
        : regexRGB.test(color)
        ? RGBToHSL(color)
        : regexHSL.test(color) && regexHSL.exec(color)?.slice(1);
    let newHsl = '';

    if (hsl && hsl[2] && parseInt(hsl[2]) > 50) {
        newHsl = 'hsl(' + hsl[0] + ',' + hsl[1] + '%,' + (parseInt(hsl[2]) - 10) + '%)';
    } else if (hsl && hsl[2] && parseInt(hsl[2]) < 50) {
        newHsl = 'hsl(' + hsl[0] + ',' + hsl[1] + '%,' + (parseInt(hsl[2]) + 10) + '%)';
    } else if (hsl && hsl[2] && parseInt(hsl[2]) === 50) {
        newHsl = 'hsl(' + hsl[0] + ',' + hsl[1] + '%,' + (parseInt(hsl[2]) + 10) + '%)';
    }
    return newHsl;
};

const brightness = (color: string): number => {
    const regexHEX = new RegExp(HEXRegexString);
    const regexRGB = new RegExp(RGBRegexString);
    const regexHSL = new RegExp(HSLRegexString);
    const hsl: undefined | boolean | any[] = regexHEX.test(color)
        ? hexToHSL(color)
        : regexRGB.test(color)
        ? RGBToHSL(color)
        : regexHSL.test(color) && regexHSL.exec(color)?.slice(1);

    return hsl ? +hsl[2] : -1;
};

export default { getLightShade, getDarkShade, colorContrast, brightness };
